/**
 * Patch the Duration into a MediaRecorder WebM blob.
 *
 * MediaRecorder writes its Segment with an "unknown" size (0x01FFFFFFFFFFFFFF)
 * and omits `Info > Duration` entirely, because it is streaming and does not
 * know how long the recording will be until it ends. Players cope by treating
 * duration as Infinity, which breaks the scrubber: you cannot seek in the
 * local preview, and `video.duration` reads as Infinity.
 *
 * This rewrites the container: parse the top level, find `Segment`, walk its
 * children far enough to find (or synthesise) `Info`, set `Duration` from the
 * value we measured with our own clock, and re-serialise every affected
 * element with a known size.
 *
 * Scope, deliberately:
 *   - This runs on the *local preview blob only*. Slate uploads parts while
 *     recording, so the bytes in S3 are already gone by the time we know the
 *     duration. It does not matter, because MediaConvert writes correct
 *     metadata on the transcoded output, which is what everyone else plays.
 *     The client-reported `durationSec` on /complete is advisory for the same
 *     reason.
 *   - Anything unexpected returns the original blob. A preview that scrubs
 *     badly is a papercut; a preview that fails to load is a lost recording.
 *
 * EBML primer, enough to read the code below:
 *   Every element is [id][size][payload]. Both id and size are variable-length
 *   integers; the first byte's leading-zero count gives the total length. Size
 *   values additionally strip the marker bit. A size whose value bits are all
 *   ones means "unknown length": parse until the parent ends.
 */

const ID_EBML = 0x1a45dfa3;
const ID_SEGMENT = 0x18538067;
const ID_INFO = 0x1549a966;
const ID_TIMECODE_SCALE = 0x2ad7b1;
const ID_DURATION = 0x4489;

const DEFAULT_TIMECODE_SCALE = 1_000_000; // 1ms, MediaRecorder's value.

interface Element {
  id: number;
  /** Offset of the first payload byte. */
  dataStart: number;
  /** Offset one past the last payload byte. */
  dataEnd: number;
  /** Offset of the first byte of the element (its id). */
  start: number;
  unknownSize: boolean;
}

class Reader {
  private offset = 0;

  constructor(private readonly view: DataView) {}

  get position(): number {
    return this.offset;
  }

  set position(value: number) {
    this.offset = value;
  }

  get atEnd(): boolean {
    return this.offset >= this.view.byteLength;
  }

  /** Element ids keep their marker bit; that is what makes them ids. */
  readId(): number {
    const first = this.view.getUint8(this.offset);
    const length = leadingLength(first);
    if (length === 0 || this.offset + length > this.view.byteLength) {
      throw new Error('bad ebml id');
    }
    let value = 0;
    for (let index = 0; index < length; index += 1) {
      value = value * 256 + this.view.getUint8(this.offset + index);
    }
    this.offset += length;
    return value;
  }

  /** Sizes drop the marker bit. Returns null for the "unknown size" form. */
  readSize(): number | null {
    const first = this.view.getUint8(this.offset);
    const length = leadingLength(first);
    if (length === 0 || this.offset + length > this.view.byteLength) {
      throw new Error('bad ebml size');
    }
    let value = first & (0xff >> length);
    let allOnes = value === 0xff >> length;
    for (let index = 1; index < length; index += 1) {
      const byte = this.view.getUint8(this.offset + index);
      allOnes = allOnes && byte === 0xff;
      value = value * 256 + byte;
    }
    this.offset += length;
    return allOnes ? null : value;
  }

  readElement(limit: number): Element | null {
    if (this.offset >= limit) return null;
    const start = this.offset;
    const id = this.readId();
    const size = this.readSize();
    const dataStart = this.offset;
    const dataEnd = size === null ? limit : Math.min(dataStart + size, limit);
    this.offset = dataEnd;
    return { id, start, dataStart, dataEnd, unknownSize: size === null };
  }
}

/** Number of bytes in a vint whose first byte is `first`. 0 when invalid. */
function leadingLength(first: number): number {
  if (first === 0) return 0;
  let mask = 0x80;
  for (let length = 1; length <= 8; length += 1) {
    if ((first & mask) !== 0) return length;
    mask >>= 1;
  }
  return 0;
}

/** Encode an EBML size as the shortest vint that fits. */
function encodeSize(value: number) {
  for (let length = 1; length <= 8; length += 1) {
    const capacity = 2 ** (7 * length) - 1;
    if (value < capacity) {
      const out = new Uint8Array(length);
      let remaining = value;
      for (let index = length - 1; index >= 0; index -= 1) {
        out[index] = remaining % 256;
        remaining = Math.floor(remaining / 256);
      }
      out[0] |= 0x80 >> (length - 1);
      return out;
    }
  }
  throw new Error('size too large');
}

/** Encode an element id back to its bytes (the marker bit is already in it). */
function encodeId(id: number) {
  const bytes: number[] = [];
  let remaining = id;
  while (remaining > 0) {
    bytes.unshift(remaining % 256);
    remaining = Math.floor(remaining / 256);
  }
  return Uint8Array.from(bytes.length > 0 ? bytes : [0]);
}

function element(id: number, payload: Uint8Array) {
  const idBytes = encodeId(id);
  const sizeBytes = encodeSize(payload.byteLength);
  const out = new Uint8Array(idBytes.byteLength + sizeBytes.byteLength + payload.byteLength);
  out.set(idBytes, 0);
  out.set(sizeBytes, idBytes.byteLength);
  out.set(payload, idBytes.byteLength + sizeBytes.byteLength);
  return out;
}

function float64(value: number) {
  const out = new Uint8Array(8);
  new DataView(out.buffer).setFloat64(0, value, false);
  return out;
}

function readUint(view: DataView, from: number, to: number): number {
  let value = 0;
  for (let index = from; index < to; index += 1) value = value * 256 + view.getUint8(index);
  return value;
}

// The return type is deliberately inferred rather than written as
// `Uint8Array`: since TypeScript 5.7 typed arrays are generic over their
// backing buffer, and the bare alias widens to `ArrayBufferLike`, which is not
// a valid `BlobPart` (a SharedArrayBuffer cannot back a Blob). Letting
// inference produce `Uint8Array<ArrayBuffer>` keeps the Blob constructor happy.
function concat(chunks: Uint8Array[]) {
  const total = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return out;
}

function slice(source: Uint8Array, from: number, to: number): Uint8Array {
  return source.subarray(from, to);
}

/**
 * Returns a new Blob whose Segment > Info > Duration is `durationMs`, or the
 * original blob if the bytes are not a WebM we recognise.
 */
export async function fixWebmDuration(blob: Blob, durationMs: number): Promise<Blob> {
  if (!Number.isFinite(durationMs) || durationMs <= 0) return blob;
  if (!blob.type.includes('webm')) return blob;

  try {
    const buffer = new Uint8Array(await blob.arrayBuffer());
    const view = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength);
    const reader = new Reader(view);

    // ---- top level: [EBML header][Segment] --------------------------------
    let segment: Element | null = null;
    const preamble: Uint8Array[] = [];
    while (!reader.atEnd) {
      const top = reader.readElement(buffer.byteLength);
      if (!top) break;
      if (top.id === ID_SEGMENT) {
        segment = top;
        break;
      }
      if (top.id !== ID_EBML && preamble.length === 0) {
        // Not a WebM/Matroska stream at all.
        return blob;
      }
      preamble.push(slice(buffer, top.start, top.dataEnd));
    }
    if (!segment) return blob;

    // ---- Segment children: find Info, keep everything else verbatim -------
    const child = new Reader(view);
    child.position = segment.dataStart;

    const before: Uint8Array[] = [];
    const after: Uint8Array[] = [];
    let info: Element | null = null;

    while (child.position < segment.dataEnd) {
      const node = child.readElement(segment.dataEnd);
      if (!node) break;
      // A nested unknown-size element means the stream is truncated in a way
      // we should not try to reassemble.
      if (node.unknownSize) return blob;
      if (node.id === ID_INFO && !info) {
        info = node;
        continue;
      }
      (info ? after : before).push(slice(buffer, node.start, node.dataEnd));
    }

    // ---- rebuild Info -----------------------------------------------------
    let timecodeScale = DEFAULT_TIMECODE_SCALE;
    const infoChildren: Uint8Array[] = [];

    if (info) {
      const infoReader = new Reader(view);
      infoReader.position = info.dataStart;
      while (infoReader.position < info.dataEnd) {
        const node = infoReader.readElement(info.dataEnd);
        if (!node) break;
        if (node.id === ID_TIMECODE_SCALE) {
          const parsed = readUint(view, node.dataStart, node.dataEnd);
          if (parsed > 0) timecodeScale = parsed;
        }
        // Drop any existing Duration; we are about to write the real one.
        if (node.id === ID_DURATION) continue;
        infoChildren.push(slice(buffer, node.start, node.dataEnd));
      }
    } else {
      infoChildren.push(element(ID_TIMECODE_SCALE, Uint8Array.from([0x0f, 0x42, 0x40])));
    }

    // Duration is expressed in TimecodeScale units, which for MediaRecorder is
    // milliseconds, but read the real scale rather than assuming it.
    const scaledDuration = (durationMs * 1_000_000) / timecodeScale;
    infoChildren.push(element(ID_DURATION, float64(scaledDuration)));

    const rebuiltInfo = element(ID_INFO, concat(infoChildren));
    const segmentBody = concat([...before, rebuiltInfo, ...after]);
    const rebuiltSegment = element(ID_SEGMENT, segmentBody);

    return new Blob([concat([...preamble, rebuiltSegment])], { type: blob.type });
  } catch {
    // Malformed, truncated, or simply not shaped the way we expect.
    return blob;
  }
}
